import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getForm, listSubmissions, exportSubmissions } from '../lib/api';
import type { Form, FormField } from '../types/form';
import type { Submission } from '../types/submission';
import { Skeleton, SkeletonSubmissionsTable } from '../components/ui/Skeleton';

export default function SubmissionsPage() {
  const { id } = useParams<{ id: string }>();
  const [form, setForm] = useState<Form | null>(null);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!id) return;

    async function loadData() {
      try {
        setLoading(true);
        const [formData, submissionsData] = await Promise.all([
          getForm(id!),
          listSubmissions(id!),
        ]);
        setForm(formData);
        setSubmissions(submissionsData.submissions);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [id]);

  async function handleExport(format: 'json' | 'csv') {
    if (!id) return;

    try {
      setExporting(true);
      const result = await exportSubmissions(id, format);

      if (format === 'csv' && result instanceof Blob) {
        const url = URL.createObjectURL(result);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${form?.name || 'submissions'}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        const blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${form?.name || 'submissions'}.json`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  }

  function getFieldLabel(fieldName: string): string {
    if (!form) return fieldName;
    const field = form.schema.fields.find((f: FormField) => f.name === fieldName);
    return field?.label || fieldName;
  }

  function getFieldType(fieldName: string): string {
    if (!form) return 'text';
    const field = form.schema.fields.find((f: FormField) => f.name === fieldName);
    return field?.type || 'text';
  }

  function renderValue(value: unknown, fieldType: string): React.ReactNode {
    if (value === null || value === undefined) return '-';

    // Render signature as an image
    if (fieldType === 'signature' && typeof value === 'string' && value.startsWith('data:image')) {
      return (
        <img
          src={value}
          alt="Signature"
          className="h-12 w-auto max-w-[200px] border border-gray-200 rounded bg-white"
        />
      );
    }

    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }

  if (loading) {
    return (
      <div>
        {/* Header Skeleton */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Skeleton className="w-6 h-6 rounded" />
            <div>
              <Skeleton className="h-8 w-64 mb-2" />
              <Skeleton className="h-4 w-32" />
            </div>
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-10 w-28 rounded-lg" />
            <Skeleton className="h-10 w-28 rounded-lg" />
          </div>
        </div>
        <SkeletonSubmissionsTable rows={8} columns={5} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <Link to="/formspace" className="btn-primary">
          Back to Forms
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Link to="/formspace" className="text-gray-500 hover:text-gray-700">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{form?.name} - Submissions</h1>
            <p className="text-gray-500">{submissions.length} submissions</p>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => handleExport('csv')}
            disabled={exporting || submissions.length === 0}
            className="btn-secondary"
          >
            Export CSV
          </button>
          <button
            onClick={() => handleExport('json')}
            disabled={exporting || submissions.length === 0}
            className="btn-secondary"
          >
            Export JSON
          </button>
        </div>
      </div>

      {/* Submissions List */}
      {submissions.length === 0 ? (
        <div className="card p-12 text-center">
          <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No submissions yet</h3>
          <p className="text-gray-500 mb-6">Submissions will appear here once your form receives responses</p>
          {form?.status === 'published' && (
            <a
              href={`/f/${form.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
            >
              View Public Form
            </a>
          )}
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                {form?.schema.fields.slice(0, 3).map((field: FormField) => (
                  <th
                    key={field.id}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {field.label}
                  </th>
                ))}
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {submissions.map((submission) => (
                <tr key={submission.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(submission.created_at).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        submission.status === 'new'
                          ? 'bg-blue-100 text-blue-800'
                          : submission.status === 'reviewed'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {submission.status}
                    </span>
                  </td>
                  {form?.schema.fields.slice(0, 3).map((field: FormField) => (
                    <td
                      key={field.id}
                      className={`px-6 py-4 text-sm text-gray-900 ${field.type === 'signature' ? '' : 'whitespace-nowrap max-w-xs truncate'}`}
                    >
                      {renderValue(submission.data[field.name], field.type)}
                    </td>
                  ))}
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                    <button
                      onClick={() => setSelectedSubmission(submission)}
                      className="text-primary-600 hover:text-primary-800 mr-3"
                    >
                      View
                    </button>
                    <a
                      href={`/api/forms/${id}/submissions/${submission.id}/pdf`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-500 hover:text-gray-700"
                      title="Download PDF"
                    >
                      <svg className="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Submission Detail Modal */}
      {selectedSubmission && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Submission Details</h2>
                <p className="text-sm text-gray-500">
                  {new Date(selectedSubmission.created_at).toLocaleString()}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <a
                  href={`/api/forms/${id}/submissions/${selectedSubmission.id}/pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Download PDF"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  PDF
                </a>
                <button
                  onClick={() => setSelectedSubmission(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <dl className="space-y-4">
                {Object.entries(selectedSubmission.data).map(([key, value]) => (
                  <div key={key} className="border-b border-gray-100 pb-4">
                    <dt className="text-sm font-medium text-gray-500 mb-1">
                      {getFieldLabel(key)}
                    </dt>
                    <dd className="text-sm text-gray-900">
                      {renderValue(value, getFieldType(key))}
                    </dd>
                  </div>
                ))}
              </dl>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Metadata</h3>
                <dl className="text-xs text-gray-500 space-y-1">
                  <div>
                    <dt className="inline font-medium">Submitted:</dt>{' '}
                    <dd className="inline">{selectedSubmission.metadata.submittedAt}</dd>
                  </div>
                  {selectedSubmission.metadata.ip && (
                    <div>
                      <dt className="inline font-medium">IP:</dt>{' '}
                      <dd className="inline">{selectedSubmission.metadata.ip}</dd>
                    </div>
                  )}
                </dl>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
